import { useCallback, useEffect, useState } from "react";
import { API } from "@/lib/api";

const SW_PATH = "/rider-sw.js";

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) output[i] = rawData.charCodeAt(i);
  return output;
};

export const isPushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

// iOS only lets PWAs (added-to-Home-Screen) use Web Push. A regular Safari tab
// won't ever deliver a push, so we need to detect this and tell the rider to
// install the app first. Requires iOS 16.4+.
export const isIOS = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOSUA = /iPad|iPhone|iPod/.test(ua);
  // iPads on iOS 13+ report as Mac — detect via touch + platform.
  const iPadOS = navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
  return iOSUA || iPadOS;
};

export const isStandalonePWA = () => {
  if (typeof window === "undefined") return false;
  // iOS Safari exposes `navigator.standalone` when launched from Home Screen.
  // Other browsers use the display-mode media query.
  if (window.navigator.standalone === true) return true;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  return false;
};

// True when we're in a regular iOS Safari tab — push will never work until
// the rider installs to Home Screen.
export const needsIOSInstall = () => isIOS() && !isStandalonePWA();

const claimSubscriptionForRider = async (riderId, sub) => {
  if (!riderId || !sub) return false;
  const subJson = sub.toJSON();
  const res = await fetch(`${API}/riders/${riderId}/push/subscribe`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      endpoint: subJson.endpoint,
      keys: subJson.keys,
      user_agent: navigator.userAgent,
    }),
  });
  return res.ok;
};

/**
 * Manages the rider's Web Push subscription lifecycle.
 *
 * Flow:
 *   1. Register the service worker once on mount.
 *   2. Read current Notification.permission to drive UI state.
 *   3. If a subscription already exists in this browser, re-claim it for the
 *      currently-logged-in rider (handles a different rider logging in on the
 *      same phone — the endpoint gets rebound to the new rider on the backend).
 *   4. enable() → request permission → fetch VAPID key → subscribe → POST to backend.
 *   5. disable() → unsubscribe locally and tell the backend to forget the endpoint.
 */
export const useRiderPush = (riderId) => {
  const supported = isPushSupported();
  const iosNeedsInstall = needsIOSInstall();
  const [permission, setPermission] = useState(
    supported ? Notification.permission : "denied"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  // Keep `subscribed` in sync with what the browser actually has, AND
  // re-bind any existing subscription to the currently-logged-in rider so
  // shared/passed-around devices route notifications to the right person.
  useEffect(() => {
    if (!supported || !riderId) return;
    let cancelled = false;
    (async () => {
      try {
        // updateViaCache: 'none' so a new rider-sw.js (e.g. with fixed
        // notification options for lock-screen behavior) is picked up on the
        // next page load instead of being served from HTTP cache for up to 24h.
        const reg = await navigator.serviceWorker.register(SW_PATH, {
          updateViaCache: "none",
        });
        // Force an immediate update check so riders who already have the
        // older SW installed get the new push handler without waiting.
        reg.update().catch(() => undefined);
        const existing = await reg.pushManager.getSubscription();
        if (cancelled) return;
        if (existing) {
          // Re-POST so the backend's `push_subscriptions` row is rebound from
          // any previous rider to the current one. Upsert is keyed on endpoint.
          await claimSubscriptionForRider(riderId, existing).catch(() => undefined);
          if (!cancelled) setSubscribed(true);
        } else {
          setSubscribed(false);
        }
      } catch (e) {
        // SW unavailable (e.g. plain http) — treat as unsupported.
        if (!cancelled) setSubscribed(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [supported, riderId]);

  const enable = useCallback(async () => {
    if (iosNeedsInstall) {
      throw new Error(
        "On iPhone, tap the Share button in Safari and choose 'Add to Home Screen'. Then open Pokus from the Home Screen and try again."
      );
    }
    if (!supported) {
      throw new Error("Notifications are not supported in this browser.");
    }
    if (!riderId) throw new Error("Rider not loaded yet.");
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        throw new Error(
          perm === "denied"
            ? "Notifications are blocked. Enable them from your browser settings."
            : "Notification permission was not granted."
        );
      }

      const keyRes = await fetch(`${API}/riders/push/public-key`);
      if (!keyRes.ok) {
        const detail = await keyRes.text();
        throw new Error(
          `Push notifications are not configured on the server. ${detail}`.trim()
        );
      }
      const { public_key: publicKey } = await keyRes.json();

      const reg = await navigator.serviceWorker.register(SW_PATH, {
        updateViaCache: "none",
      });
      await navigator.serviceWorker.ready;

      // Reuse an existing subscription if present so the backend stays in sync.
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }

      const ok = await claimSubscriptionForRider(riderId, sub);
      if (!ok) {
        throw new Error("Failed to register push subscription");
      }

      setSubscribed(true);

      // Send a confirmation push so the rider sees it actually works.
      fetch(`${API}/riders/${riderId}/push/test`, { method: "POST" }).catch(
        () => undefined
      );

      return true;
    } finally {
      setBusy(false);
    }
  }, [supported, riderId]);

  const disable = useCallback(async () => {
    if (!supported) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe().catch(() => undefined);
        if (riderId) {
          await fetch(`${API}/riders/${riderId}/push/unsubscribe`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ endpoint }),
          }).catch(() => undefined);
        }
      }
      setSubscribed(false);
    } finally {
      setBusy(false);
    }
  }, [supported, riderId]);

  return {
    supported,
    permission,
    subscribed,
    busy,
    enable,
    disable,
    iosNeedsInstall,
  };
};
